/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2023, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import axios from 'axios';
import { StatusCodes } from 'http-status-codes';
import urlJoin from 'proper-url-join';

import { endpoints } from '../../../ia/util/config';
import { generateSubscribeParams } from './utils';

const endpointIntegrations = (assistantId: string, environmentId: string) =>
  urlJoin(
    endpoints.store,
    '/v2/assistants/',
    assistantId,
    '/environments/',
    environmentId,
    '/integrations'
  );

const endpointIntegration = (
  assistantId: string,
  environmentId: string,
  integrationId: string
) =>
  urlJoin(
    endpoints.store,
    '/v2/assistants/',
    assistantId,
    '/environments/',
    environmentId,
    '/integrations',
    integrationId
  );

const endpointServiceDesk = (
  channelType: string,
  integrationId: string,
  endpoint: string
) =>
  urlJoin(
    endpoints.store,
    '/v2/service_desks/',
    channelType,
    integrationId,
    endpoint
  );

export const listIntegrations = (
  assistantId: string,
  environmentId: string
): Promise<any> =>
  axios
    .get(endpointIntegrations(assistantId, environmentId), {
      params: {
        verbose: true
      }
    })
    .then(response => response.data);

export const getIntegration = (
  assistantId: string,
  environmentId: string,
  integrationId: string
): Promise<any> =>
  axios
    .get(endpointIntegration(assistantId, environmentId, integrationId), {
      params: {
        verbose: true
      }
    })
    .then(response => response.data);

export const createIntegration = (
  assistantId: string,
  environmentId: string,
  integration: any
): Promise<any> =>
  axios
    .post(endpointIntegrations(assistantId, environmentId), integration, {
      params: {
        verbose: true
      }
    })
    .then(response => response.data);

export const updateIntegration = (
  assistantId: string,
  environmentId: string,
  integrationId: string,
  integration: any
): Promise<any> =>
  axios
    .post(
      endpointIntegration(assistantId, environmentId, integrationId),
      integration,
      {
        params: {
          verbose: true
        }
      }
    )
    .then(response => response.data);

export const deleteIntegration = (
  assistantId: string,
  environmentId: string,
  integrationId: string
): Promise<any> => {
  // If deleting returns a 404, just move on with your life.
  return axios
    .delete(endpointIntegration(assistantId, environmentId, integrationId), {
      validateStatus: status =>
        status >= StatusCodes.OK ||
        status < StatusCodes.MULTIPLE_CHOICES ||
        status === StatusCodes.NOT_FOUND,
      params: {
        verbose: true
      }
    })
    .then(response => response.data);
};

// For calls like /subscribe do not log-off user or show /unauthorized screen.
// For that purpose, adding interceptor here which would handle 403/401s separately
// Add a response interceptor

export const subscribe = (
  channelType: string,
  integrationId: string,
  data?: any,
  generateEncryptionKey?: boolean
): Promise<any> => {
  return axios
    .post(endpointServiceDesk(channelType, integrationId, 'subscribe'), data, {
      params: generateSubscribeParams(generateEncryptionKey)
    })
    .then(response => response.data);
};

export const unsubscribe = (
  channelType: string,
  integrationId: string,
  data?: any
): Promise<any> =>
  axios
    .post(
      endpointServiceDesk(channelType, integrationId, 'unsubscribe'),
      data,
      {
        params: generateSubscribeParams(false)
      }
    )
    .then(response => response.data);
